import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Button,
  TextField,
  Tooltip,
  Divider,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import ImageIcon from "@mui/icons-material/Image";

const PLACEHOLDERS = ["{{.name}}"];

const ToolbarButton = ({ title, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      sx={{
        borderRadius: "8px",
        color: "text.secondary",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

const EmailEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const savedRange = useRef(null);
  const isInternalChange = useRef(false);

  const [showImageInput, setShowImageInput] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const exec = (command) => {
    editorRef.current.focus();
    restoreSelection();
    document.execCommand(command, false, null);
  };

  const insertHTML = (html) => {
    editorRef.current.focus();
    restoreSelection();
    document.execCommand("insertHTML", false, html);
    onChange(editorRef.current.innerHTML);
  };

  const insertImage = () => {
    if (!imageUrl) return;

    const safeUrl = imageUrl.trim();

    if (!safeUrl.startsWith("http")) {
      alert("Please enter a valid hosted image URL (https://...)");
      return;
    }

    insertHTML(
      `<img src="${safeUrl}" alt="Image" style="max-width:100%; height:auto;" />`
    );

    setImageUrl("");
    setShowImageInput(false);
  };

  useEffect(() => {
    if (!editorRef.current) return;
    if (!isInternalChange.current) {
      const raw = value || "";
      // Plain text with newlines won't render blank lines via innerHTML unless converted.
      if (raw && !/<[a-z][\s\S]*>/i.test(raw)) {
        editorRef.current.innerHTML = raw
          .split(/\n/)
          .map((line) => `<div>${line || "<br>"}</div>`)
          .join("");
      } else {
        editorRef.current.innerHTML = raw;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = () => {
    isInternalChange.current = true;
    onChange(editorRef.current.innerHTML);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: "#fff",
        "&:focus-within": {
          borderColor: "primary.main",
          boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}22`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          alignItems: "center",
          px: 1,
          py: 0.75,
          bgcolor: "#fafafa",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <ToolbarButton title="Bold" onClick={() => exec("bold")}>
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton title="Italic" onClick={() => exec("italic")}>
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Add image"
          onClick={() => setShowImageInput((prev) => !prev)}
        >
          <ImageIcon fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {PLACEHOLDERS.map((ph) => (
          <Button
            key={ph}
            size="small"
            variant="outlined"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertHTML(ph)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              py: 0.25,
            }}
          >
            {ph}
          </Button>
        ))}
      </Box>

      {showImageInput ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            p: 1.5,
            bgcolor: "#fafafa",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="https://yourdomain.com/image.png"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
          <Button
            variant="contained"
            onClick={insertImage}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              whiteSpace: "nowrap",
            }}
          >
            Insert
          </Button>
        </Stack>
      ) : null}

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        sx={{
          minHeight: 140,
          p: 2,
          fontFamily: "inherit",
          cursor: "text",
          outline: "none",
          lineHeight: 1.45,
          "& img": {
            maxWidth: "100%",
            display: "block",
            margin: "8px 0",
          },
          "& p": {
            margin: "0 0 2px",
          },
          "& p:last-child": {
            marginBottom: 0,
          },
          "& div": {
            margin: 0,
            lineHeight: 1.45,
          },
        }}
      />
    </Box>
  );
};

export default EmailEditor;
